import { ExchangeFunctionResponse } from '../types';

/**
 * Service pour les appels à l'Azure Function PowerShell
 * Les opérations Exchange Online reposent sur des cmdlets qui n'ont pas
 * d'équivalent dans Graph API (boîte partagée, FullAccess, transfert SMTP).
 *
 * Authentification : token Entra d'audience api://<clientId>, validé par Easy Auth
 * côté Function App. Ce n'est PAS le token Graph — voir getFunctionToken().
 */

const FUNCTION_URL = process.env.REACT_APP_FUNCTION_URL || '';

/** Appel générique à l'Azure Function */
async function callExchangeFunction(
  action: string,
  params: Record<string, string>,
  functionToken: string
): Promise<ExchangeFunctionResponse> {
  if (!FUNCTION_URL) {
    throw new Error(
      "REACT_APP_FUNCTION_URL n'est pas configuré : les actions Exchange sont indisponibles."
    );
  }

  const response = await fetch(`${FUNCTION_URL}/api/ExchangeOffboarding`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${functionToken}`,
    },
    body: JSON.stringify({ action, ...params }),
  });

  const rawBody = await response.text();

  // La Function renvoie {success,action,message} y compris en 500 : on remonte le
  // message Exchange plutôt que le JSON brut, sinon l'admin lit du bruit.
  let parsed: Partial<ExchangeFunctionResponse> | null = null;
  try {
    parsed = rawBody ? JSON.parse(rawBody) : null;
  } catch {
    parsed = null;
  }

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error(
        "Accès refusé par la Function Exchange (401). Vérifier que le compte est autorisé sur l'App Registration IT-Portal-Tool."
      );
    }
    throw new Error(
      parsed?.message || `Erreur Azure Function (${response.status})${rawBody ? ` : ${rawBody}` : ''}`
    );
  }

  if (parsed && parsed.success === false) {
    throw new Error(parsed.message || 'Erreur Exchange inconnue');
  }

  return parsed as ExchangeFunctionResponse;
}

/**
 * Diagnostic sans effet de bord : vérifie que la chaîne Exchange répond.
 * Avec une identité, teste en plus la LECTURE de la boîte — ce qui distingue un
 * problème de connexion d'un manque de droits sur les boîtes, les deux se
 * présentant sous le même « UnAuthorized ».
 */
export async function pingExchange(
  functionToken: string,
  identity?: string
): Promise<ExchangeFunctionResponse> {
  return callExchangeFunction('ping', identity ? { identity } : {}, functionToken);
}

/** Convertir la boîte mail en SharedMailbox */
export async function convertToSharedMailbox(
  email: string,
  functionToken: string
): Promise<ExchangeFunctionResponse> {
  return callExchangeFunction('convertToShared', { identity: email }, functionToken);
}

/** Masquer la boîte mail de la GAL (Global Address List) */
export async function hideFromGAL(
  email: string,
  functionToken: string
): Promise<ExchangeFunctionResponse> {
  return callExchangeFunction('hideFromGAL', { identity: email }, functionToken);
}

/** Déléguer l'accès à la boîte mail */
export async function delegateMailbox(
  email: string,
  delegateEmail: string,
  functionToken: string
): Promise<ExchangeFunctionResponse> {
  return callExchangeFunction(
    'delegateMailbox',
    { identity: email, delegateTo: delegateEmail },
    functionToken
  );
}

/** Configurer le transfert de mails */
export async function setMailForwarding(
  email: string,
  forwardTo: string,
  functionToken: string
): Promise<ExchangeFunctionResponse> {
  return callExchangeFunction(
    'setForwarding',
    { identity: email, forwardTo },
    functionToken
  );
}

/** État complet de la boîte : absence, transfert, délégations */
export async function getMailboxSettings(
  email: string,
  functionToken: string
): Promise<ExchangeFunctionResponse> {
  return callExchangeFunction('getMailboxSettings', { identity: email }, functionToken);
}

/** Désactiver le message d'absence en cours */
export async function disableAutoReply(
  email: string,
  functionToken: string
): Promise<ExchangeFunctionResponse> {
  return callExchangeFunction('disableAutoReply', { identity: email }, functionToken);
}

/** Supprimer le transfert configuré sur la boîte */
export async function removeForwarding(
  email: string,
  functionToken: string
): Promise<ExchangeFunctionResponse> {
  return callExchangeFunction('removeForwarding', { identity: email }, functionToken);
}

/** Révoquer l'accès FullAccess d'un délégué */
export async function removeDelegate(
  email: string,
  delegateEmail: string,
  functionToken: string
): Promise<ExchangeFunctionResponse> {
  return callExchangeFunction(
    'removeDelegate',
    { identity: email, delegateTo: delegateEmail },
    functionToken
  );
}

/**
 * Configurer le message d'absence.
 * Passe par Exchange et non par Graph : en délégué, un Exchange Administrator n'a
 * pas accès à la boîte d'un tiers et PATCH /users/{id}/mailboxSettings est refusé.
 */
export async function setAutoReply(
  email: string,
  message: string,
  functionToken: string
): Promise<ExchangeFunctionResponse> {
  return callExchangeFunction('setAutoReply', { identity: email, message }, functionToken);
}
